import { useCallback, useRef, useState } from 'react'

/**
 * Live rendered height of a pinned (fixed-position) element, for callers that
 * must reserve an equal amount of space at the end of a scrolling flow.
 *
 * Attachment is driven by a **callback ref, not by an effect**, and that is the
 * whole point of this hook. The element being measured routinely mounts later
 * than the component reserving space for it — the deck feed renders a loading
 * skeleton on first commit and the pinned element only appears once cards have
 * arrived. An effect keyed on a "should this element exist" flag runs while
 * that flag has already settled and `ref.current` is still null, so the
 * observer never attaches and the reserved space stays 0 for the life of the
 * screen. A callback ref runs when the node actually enters the DOM, whenever
 * that happens, which makes attachment independent of mount ordering rather
 * than special-casing the loading state.
 *
 * Returns the element's **border-box** height — the space it actually occupies
 * — so no caller ever needs a constant standing in for it (ADR-067 forbids a
 * hand-tuned constant here, and the elements measured have variable height:
 * multi-line growth, keyboard raise, progress banners).
 *
 * Height is 0 whenever no element is attached, so a surface with no pinned
 * element gains no phantom padding.
 *
 * Positioning is deliberately not this hook's business — it measures height
 * only. See docs/diagnostics/composer-keyboard-positioning-measurement-2026-07-26.md
 * for the positioning mechanism, which is separate and unchanged.
 */
export function useMeasuredHeight() {
  const [height, setHeight] = useState(0)
  const observerRef = useRef<ResizeObserver | null>(null)

  const ref = useCallback((node: HTMLElement | null) => {
    observerRef.current?.disconnect()
    observerRef.current = null

    if (!node) {
      setHeight(0)
      return
    }

    // Seed synchronously: ResizeObserver's first callback lands a frame later,
    // and the caller would otherwise reserve 0 for that frame.
    setHeight(node.getBoundingClientRect().height)

    // jsdom and SSR have no ResizeObserver; the seeded height above is all
    // those environments can offer, and constructing it unguarded would throw.
    if (typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (!entry) return
      // borderBoxSize, not contentRect: contentRect excludes padding and
      // border, which understates the space the element occupies. The fallback
      // covers engines predating borderBoxSize.
      const borderBox = entry.borderBoxSize?.[0]?.blockSize
      setHeight(borderBox ?? entry.target.getBoundingClientRect().height)
    })
    observer.observe(node)
    observerRef.current = observer
  }, [])

  return [ref, height] as const
}
